"use client";

import React, { useState, useEffect, useRef } from "react";
import { Menu, Search, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { cn } from "@/lib/utils";
function SearchResults() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <p className="p-4 text-sm font-mono text-muted-foreground">Curating results...</p>;
  }
  if (error) {
    return <p className="p-4 text-sm font-mono text-destructive">Failed to load collection.</p>;
  }
  if (!products.length) {
    return <p className="p-4 text-sm font-mono text-muted-foreground">No matching vintages found.</p>;
  }
  return <div data-section-id="41121" className="max-h-[60vh] overflow-y-auto py-2">
      {products.map((product, i) => <Link key={product.id} to={product.link} className="flex items-center gap-3 px-4 py-2 hover:bg-accent/50 transition-colors" data-index={i}>
          {product.featured_image_url && <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-sm bg-muted shadow-sm">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" />
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-serif font-medium text-primary">{product.title}</p>
            <div className="flex items-center gap-2">
              {product.price !== undefined && <span className={cn("text-xs font-mono", product.discounted_price ? "text-muted-foreground line-through" : "text-secondary font-semibold")}>
                  ${product.price}
                </span>}
              {product.discounted_price !== undefined && <span className="text-xs font-mono font-semibold text-secondary">
                  ${product.discounted_price}
                </span>}
            </div>
          </div>
        </Link>)}
    </div>;
}
function SearchDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return <EcommerceProductsProvider wp_query={wp_query} postType="product">
      <div className="relative" ref={containerRef}>
        <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)} className="text-foreground hover:text-secondary transition-colors" aria-label="Search">
          <Search className="size-5" />
        </Button>

        {isOpen && <div className="absolute right-0 top-full mt-2 w-[300px] sm:w-[400px] rounded-sm border border-border/40 bg-background shadow-xl ring-1 ring-primary/5 z-50 overflow-hidden">
            <div className="p-3 border-b border-border/40">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search our collection..." className="w-full font-sans text-sm focus:ring-secondary" />
            </div>
            <div className="bg-background">
              {!hasSearchQuery ? <p className="p-4 text-sm font-mono text-muted-foreground">Discover our vintage selection.</p> : <SearchResults />}
            </div>
          </div>}
      </div>
    </EcommerceProductsProvider>;
}
function NavigationContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  if (loading) {
    return <>
        <div className="hidden md:flex gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-9 w-20 bg-muted/40 animate-pulse rounded-sm" data-index={i} />)}
        </div>
        <Button variant="ghost" size="icon" className="md:hidden" disabled>
          <Menu className="size-5" />
        </Button>
      </>;
  }
  return <>
      
      <div className="hidden md:block">
        <NavigationMenu viewport={false}>
          <NavigationMenuList>
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                {item.children && item.children.length > 0 ? <>
                    <NavigationMenuTrigger className="bg-transparent hover:text-secondary font-serif text-base tracking-wide transition-colors">
                      {item.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-background">
                        {item.children.map((child, j) => <li key={child.id} data-index={j}>
                            <NavigationMenuLink asChild>
                              <Link to={child.href || "#"} className="block select-none space-y-1 rounded-sm p-3 leading-none no-underline outline-none transition-all hover:bg-accent/30 hover:text-secondary focus:bg-accent/30 focus:text-secondary">
                                <div className="text-sm font-serif font-medium leading-none">{child.label}</div>
                              </Link>
                            </NavigationMenuLink>
                          </li>)}
                      </ul>
                    </NavigationMenuContent>
                  </> : <NavigationMenuLink asChild>
                    <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-sm bg-transparent px-4 py-2 text-sm font-serif font-medium tracking-wider transition-all hover:text-secondary focus:text-secondary focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:text-secondary data-[state=open]:text-secondary">
                      {item.label}
                    </Link>
                  </NavigationMenuLink>}
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-secondary" aria-label="Open menu">
            <Menu className="size-6" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-full sm:w-full p-0 flex flex-col border-none bg-background">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation Menu</SheetTitle>
          </SheetHeader>
          
          <div className="flex items-center justify-between px-4 py-4 border-b border-border/40">
            <div className="h-8 w-auto">
               <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600482/img/bumzzy_logo.png" alt="VineEssence" className="h-full w-auto object-contain" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-6 px-4">
            <MenuProvider menu_id="22" className="contents">
              <MobileMenuLinks onLinkClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </SheetContent>
      </Sheet>
    </>;
}
function MobileMenuLinks({
  onLinkClick
}: {
  onLinkClick: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  return <nav className="flex flex-col space-y-6">
      {menuItems.map((item, i) => <div key={item.id} className="space-y-3" data-index={i}>
          {item.children && item.children.length > 0 ? <div className="space-y-3">
              <div className="font-serif font-bold text-xl text-primary tracking-wide">{item.label}</div>
              <div className="pl-4 flex flex-col space-y-3 border-l-2 border-secondary/30">
                {item.children.map((child, j) => <Link key={child.id} to={child.href || "#"} className="text-muted-foreground font-serif hover:text-secondary transition-colors py-1 text-lg" onClick={onLinkClick} data-index={j}>
                    {child.label}
                  </Link>)}
              </div>
            </div> : <Link to={item.href || "#"} className="block text-xl font-serif font-medium text-primary hover:text-secondary transition-colors tracking-wide" onClick={onLinkClick}>
              {item.label}
            </Link>}
        </div>)}
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 left-0 right-0 z-40 w-full transition-all duration-500 border-b", isScrolled ? "bg-background/80 backdrop-blur-xl border-border/20 shadow-md" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 h-16 md:h-20 flex items-center justify-between gap-4">
        
        <div className="flex-shrink-0">
          <Link to="/" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600482/img/bumzzy_logo.png" alt="VineEssence" className="h-10 md:h-12 w-auto object-contain transition-transform duration-300 hover:scale-105" />
          </Link>
        </div>

        
        <div className="flex-1 flex justify-end md:justify-center">
          <MenuProvider menu_id="22" className="contents">
            <NavigationContent />
          </MenuProvider>
        </div>

        
        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          <SearchDropdown />
          <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-secondary transition-colors" />
        </div>
      </div>
    </header>;
}